import React, {useState} from 'react';
import {Form, Formik} from 'formik';
import * as yup from 'yup';
import Box from '@material-ui/core/Box/Box';
import Button from '@material-ui/core/Button';
import {useDispatch} from 'react-redux';

import {InputTextField} from '../../../@crema/components/InputTextField';
import {InputCheckbox} from '../../../@crema/components/InputCheckbox';
import {saveBuyback} from '../../../redux/actions';
import {
  usePositiveNumberValidation,
  getPositiveNumberYupSchema,
} from 'hooks/usePositiveNumberValidation';
import {useMediaQuery} from '@material-ui/core';

interface IBuyBackFormFields {
  price?: number | string;
  enabled?: boolean;
}

interface BuyBackFormProps {
  initialPrice: number;
  enabled: boolean;
  disabledForm: boolean;
}

const MAX_SAFE_VALUE = Number.MAX_SAFE_INTEGER;
const MAX_DIGITS_MESSAGE =
  'Price exceeds maximum limit (max 16 digits allowed)';

const validationSchema = yup.object({
  enabled: yup.boolean().required(),
  price: getPositiveNumberYupSchema(
    'Price required',
    1,
    MAX_SAFE_VALUE,
    MAX_DIGITS_MESSAGE,
  )
    .when('enabled', (enabled: boolean, schema: any) => {
      return enabled ? schema.required('Price required') : schema.notRequired();
    })
    .min(1, 'Price must be greater than 0'),
});

export const BuyBackForm: React.FC<BuyBackFormProps> = ({
  initialPrice,
  enabled,
  disabledForm,
}): React.ReactElement => {
  const dispatch = useDispatch();
  const [checked, setChecked] = useState(enabled);
  const {
    isInputValid,
    validateInput,
    resetValidation,
    connectFormik,
    handleKeyDown,
    handlePaste,
  } = usePositiveNumberValidation();

  const onSubmit = (data: IBuyBackFormFields): void => {
    const newEnabledValue =
      typeof data.enabled === 'undefined' ? enabled : data.enabled;
    dispatch(saveBuyback(newEnabledValue, Number(data.price)));
  };

  const onCheckboxChange = (value: boolean): void => {
    setChecked(value);
    resetValidation();
  };
  const isMobile = useMediaQuery('(max-width:767px)');

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          price: initialPrice === -1 ? '' : initialPrice,
          enabled,
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          setFieldError,
          setFieldTouched,
          isValid,
          dirty,
        }): React.ReactElement => {
          connectFormik(setFieldError, setFieldTouched);

          return (
            <Box textAlign='left' mt={8} clone>
              <Form>
                <Box
                  display='flex'
                  flexDirection={{xs: 'column', sm: 'row'}}
                  flexWrap='wrap'
                  alignItems={{sm: 'center'}}
                >
                  <Box
                    order={{xs: 1, sm: 1}}
                    mb={{xs: 0, sm: 0}}
                    display='flex'
                    justifyContent={{xs: 'center', sm: 'flex-start'}}
                    width={{xs: '100%', sm: 'auto'}}
                  >
                    <InputCheckbox
                      name='enabled'
                      label='Enabled'
                      onChange={onCheckboxChange}
                      checked={checked}
                    />
                  </Box>

                  <Box
                    order={{xs: 3, sm: 2}}
                    display='flex'
                    justifyContent={{xs: 'center', sm: 'flex-end'}}
                    flexGrow={1}
                    mt={{xs: 2, sm: 0}}
                  >
                    <Button
                      fullWidth={isMobile}
                      variant='contained'
                      color='primary'
                      disabled={
                        disabledForm ||
                        (checked && (!isInputValid || !isValid || !dirty))
                      }
                      type='submit'
                    >
                      Save
                    </Button>
                  </Box>

                  {checked ? (
                    <Box
                      order={{xs: 2, sm: 3}}
                      width={{xs: '100%', sm: '100%'}}
                      mt={{xs: 2, sm: 0}}
                      display='flex'
                    >
                      <Box width={{xs: '100%', sm: '75%'}}>
                        <InputTextField
                          fullWidth
                          type='number'
                          placeholder='Enter price'
                          name='price'
                          inputProps={{
                            'aria-label': 'naked',
                            step: 'any',
                            min: '0',
                          }}
                          onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                            e.target.select()
                          }
                          onClick={(e: any) => {
                            if (
                              e.target &&
                              typeof e.target.select === 'function'
                            ) {
                              e.target.select();
                            }
                          }}
                          onKeyDown={handleKeyDown('price')}
                          onPaste={handlePaste(
                            'price',
                            MAX_SAFE_VALUE,
                            MAX_DIGITS_MESSAGE,
                          )}
                          onChange={validateInput(
                            'price',
                            MAX_SAFE_VALUE,
                            MAX_DIGITS_MESSAGE,
                          )}
                        />
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Form>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};
